import { isOpenDeal, isOverdueDeal, STAGE_LABEL } from './logic';
import type { Deal } from './types';

// Mint-Pipeline: logic thuần của màn Cơ hội bán hàng (lọc PIC, lọc nhanh, tìm, cờ thẻ, chữ follow, nháp Hana, báo giá chưa gắn phiếu).

/** Người xem mặc định của board (PIC "Của tôi"). */
export const ME = 'DAVID';

export type QuickFilter = 'due' | 'wait' | 'rep' | 'chg';
export const QUICK: readonly { key: QuickFilter; label: string; test: (d: Deal) => boolean }[] = [
  { key: 'due', label: 'Đến hạn follow', test: (d) => isOpenDeal(d) && d.followDays !== null && d.followDays <= 0 },
  { key: 'wait', label: 'Chờ Pricing', test: (d) => d.pricingStatus === 'Đã hỏi' },
  { key: 'rep', label: 'Pricing đã trả lời', test: (d) => d.pricingStatus === 'Đã trả giá' },
  { key: 'chg', label: 'Giá đổi', test: (d) => d.priceChanged },
];

/** Lọc PIC: '__me' = của tôi · '__all' = cả nhóm · còn lại = một sale. */
export function inPic(rows: readonly Deal[], pic: string): Deal[] {
  if (pic === '__me') return rows.filter((d) => d.sale === ME);
  if (pic === '__all') return [...rows];
  return rows.filter((d) => d.sale === pic);
}

export function picOptions(rows: readonly Deal[], canTeam: boolean): { v: string; k: string }[] {
  const n = (s: string) => rows.filter((d) => d.sale === s).length;
  const out = [{ v: '__me', k: `Của tôi · ${n(ME)}` }];
  if (!canTeam) return out;
  out.push({ v: '__all', k: `Cả nhóm · ${rows.length}` });
  for (const s of [...new Set(rows.map((d) => d.sale))].filter((x) => x !== ME)) out.push({ v: s, k: `${s} · ${n(s)}` });
  return out;
}

export function searchDeals(rows: readonly Deal[], q: string): Deal[] {
  const t = q.trim().toUpperCase();
  if (!t) return [...rows];
  return rows.filter((d) => `${d.id} ${d.customer} ${d.quotes.join(' ')} ${d.subject.toUpperCase()}`.includes(t));
}

export const dealAbbr = (s: string): string => s.replace(/[^0-9A-Za-zÀ-ỹ]/g, '').slice(0, 2).toUpperCase();

/** "21/Oct/2026" → "21/Oct". */
export const dayMonth = (d: string): string => d.replace(/^(\d{1,2}\/[A-Za-z]{3})\/\d{4}/, '$1');

export type FollowTone = 'none' | 'over' | 'today' | 'normal';
/** Dòng follow trên thẻ: chữ ngắn + tông (quá hạn đỏ · hôm nay vàng · chưa đặt nhạt). */
export function followShort(d: Deal): { text: string; tone: FollowTone } {
  if (d.followDays === null) return { text: 'Chưa đặt', tone: 'none' };
  if (d.followDays < 0) return { text: `${-d.followDays} ngày`, tone: 'over' };
  if (d.followDays === 0) return { text: 'Hôm nay', tone: 'today' };
  return { text: dayMonth(d.follow), tone: 'normal' };
}
/** Nhãn đầy đủ (title khi rê chuột / cột Follow của danh sách). */
export function followLong(d: Deal): string {
  if (d.followDays === null) return 'Chưa đặt follow';
  if (d.followDays < 0) return `Quá hạn follow ${-d.followDays} ngày`;
  return d.followDays === 0 ? 'Follow hôm nay' : `Follow ${d.follow}`;
}

export type FlagTone = 'ok' | 'warn' | 'err' | 'pri';
export function dealFlags(d: Deal): { text: string; tone: FlagTone }[] {
  const out: { text: string; tone: FlagTone }[] = [];
  if (d.pricingStatus === 'Đã trả giá') out.push({ text: 'Pricing đã trả giá', tone: 'ok' });
  if (d.pricingStatus === 'Đã hỏi') out.push({ text: 'Chờ Pricing', tone: 'warn' });
  if (d.priceChanged) out.push({ text: 'Giá đổi', tone: 'err' });
  if (d.hasMail) out.push({ text: 'Mail gốc', tone: 'pri' });
  return out;
}

export const sumOpen = (rows: readonly Deal[]): number => rows.filter(isOpenDeal).length;
export const sumOverdue = (rows: readonly Deal[]): number => rows.filter(isOverdueDeal).length;

/** Nút chuyển giai đoạn trên thẻ: Mới → Chờ giá → Đã báo → Won (Lost không có nút tới). */
const FLOW = ['New', 'Pricing', 'Quoted', 'Won'] as const;
export function neighbours(d: Deal): { prev: string | null; next: string | null } {
  const i = FLOW.findIndex((x) => x === d.stage);
  const prev = i > 0 ? FLOW[i - 1] : undefined;
  const next = i >= 0 ? FLOW[i + 1] : undefined;
  return { prev: prev ? STAGE_LABEL[prev] : null, next: next ? STAGE_LABEL[next] : null };
}

/** Nháp Hana đề xuất từ mail / Zalo (dữ liệu mẫu của board). */
export interface DraftSuggestion { id: string; src: 'Mail' | 'Zalo'; customer: string; text: string }
export const DRAFTS: readonly DraftSuggestion[] = [
  { id: 'd1', src: 'Mail', customer: 'QUEKYCORP', text: 'Hỏi giá 2×20DC đi Port Klang, ETD đầu tháng 11' },
  { id: 'd2', src: 'Zalo', customer: 'TAMNGOC', text: 'Cần giá RF đi Busan cho tháng 11' },
];
/** Báo giá / hỏi giá chưa gắn phiếu. */
export interface LooseItem { kind: 'Báo giá' | 'Pricing'; code: string; customer: string; text: string }
export const LOOSE: readonly LooseItem[] = [
  { kind: 'Báo giá', code: 'Q26FRSGNUKB193', customer: 'GREENBUD', text: 'gửi 13/Oct/2026' },
  { kind: 'Pricing', code: 'PR-261022-101530-T2', customer: 'RUITONGVN-SC', text: 'hỏi lúc 10:15 hôm nay' },
];
